/**
 * Modelos de Mensagens — lógica PURA (sem React, sem Supabase, 100% testável).
 *
 * Um modelo é um texto reutilizável com VARIÁVEIS (`{cliente}`, `{valor}`,
 * `{numero}`, `{link}`…) que só são substituídas NO ATO DO ENVIO, com os dados
 * daquele momento. O corpo gravado no banco guarda sempre os tokens crus.
 *
 * ⚠️ ORDEM IMPORTA (a armadilha desta feature): renderize as variáveis PRIMEIRO
 * e só então codifique a URL (`buildWhatsAppUrl`/`buildMailtoUrl` fazem
 * exatamente um `encodeURIComponent`, sobre a string final). Codificar o corpo
 * do modelo e depois injetar valores — ou codificar duas vezes — quebra acentos,
 * `&`, `#` e quebras de linha, e o sintoma é uma mensagem TRUNCADA no WhatsApp
 * sem erro nenhum no app.
 *
 * NÃO confundir com `contractTemplates.ts` (modelos de CONTRATO),
 * `PDF_TEMPLATE_LABELS` (modelos de PDF) ou `companies.default_terms`.
 */

// ===========================================================================
// Categorias
// ===========================================================================

export type MessageTemplateCategory =
  | 'quote'
  | 'contract'
  | 'receipt'
  | 'service_order'
  | 'generic';

export const MESSAGE_TEMPLATE_CATEGORIES: {
  value: MessageTemplateCategory;
  label: string;
  description: string;
}[] = [
  {
    value: 'quote',
    label: 'Orçamento',
    description: 'Enviar o orçamento, cobrar retorno, fazer follow-up.',
  },
  {
    value: 'contract',
    label: 'Contrato',
    description: 'Enviar o contrato para o cliente revisar e assinar.',
  },
  {
    value: 'receipt',
    label: 'Recibo',
    description: 'Enviar o comprovante de pagamento recebido.',
  },
  {
    value: 'service_order',
    label: 'Ordem de serviço',
    description: 'Avisar sobre o agendamento ou a conclusão do serviço.',
  },
  {
    value: 'generic',
    label: 'Geral',
    description: 'Mensagens avulsas que não pertencem a um documento.',
  },
];

export function categoryLabel(category: MessageTemplateCategory | string): string {
  return (
    MESSAGE_TEMPLATE_CATEGORIES.find((c) => c.value === category)?.label ?? String(category)
  );
}

// ===========================================================================
// Catálogo de variáveis
// ===========================================================================

/** Uma variável oferecida na UI como "chip" clicável que se insere no texto. */
export interface TemplateVariable {
  /** Token como aparece no corpo, com chaves: `{cliente}`. */
  token: string;
  /** Chave usada no contexto de renderização (o token sem as chaves). */
  key: string;
  label: string;
  description: string;
}

function variable(key: string, label: string, description: string): TemplateVariable {
  return { token: `{${key}}`, key, label, description };
}

const V = {
  cliente: variable('cliente', 'Cliente', 'Nome do cliente/destinatário.'),
  numero: variable('numero', 'Número', 'Número do documento (ex.: 12).'),
  valor: variable('valor', 'Valor', 'Valor total formatado em reais.'),
  validade: variable('validade', 'Validade', 'Data de validade do orçamento.'),
  link: variable('link', 'Link', 'Link público do documento.'),
  empresa: variable('empresa', 'Empresa', 'Nome da sua empresa.'),
  data: variable('data', 'Data', 'Data do documento.'),
  titulo: variable('titulo', 'Título', 'Título do documento.'),
} as const;

/**
 * Quais variáveis a UI oferece em cada categoria. A renderização NÃO usa este
 * mapa para decidir o que substituir (ver ALL_VARIABLE_KEYS) — ele existe só
 * para orientar quem escreve o modelo.
 */
export const VARIABLES_BY_CATEGORY: Record<MessageTemplateCategory, TemplateVariable[]> = {
  // `{link}` só existe onde HÁ rota pública: orçamento (`/o/:id`) e contrato
  // (`/c/:token`) — ver src/lib/urls.ts. Recibo e ordem de serviço não têm
  // página pública, então oferecer `{link}` ali produziria sempre uma string
  // vazia (e um "Segue a OS 12: " com dois-pontos solto no fim).
  quote: [V.cliente, V.numero, V.valor, V.validade, V.link, V.empresa, V.data],
  contract: [V.cliente, V.titulo, V.link, V.empresa],
  receipt: [V.cliente, V.numero, V.valor, V.empresa, V.data],
  service_order: [V.cliente, V.numero, V.titulo, V.empresa, V.data],
  generic: [V.cliente, V.empresa, V.data],
};

/**
 * Todos os tokens que o renderer RECONHECE. Um token daqui sem valor vira string
 * vazia; um token fora daqui (erro de digitação do usuário) é mantido LITERAL,
 * para o usuário perceber o erro em vez de a mensagem sumir em silêncio.
 */
export const ALL_VARIABLE_KEYS: readonly string[] = Object.freeze(
  Array.from(new Set(Object.values(V).map((v) => v.key))),
);

// ===========================================================================
// Contexto e formatação
// ===========================================================================

/** Valores crus do documento; a formatação pt-BR acontece aqui dentro. */
export interface TemplateContext {
  cliente?: string | null;
  numero?: string | number | null;
  valor?: number | null;
  validade?: string | Date | null;
  data?: string | Date | null;
  link?: string | null;
  empresa?: string | null;
  titulo?: string | null;
}

/**
 * Moeda em pt-BR. Local de propósito: importar de `pdfTemplates/shared` traria
 * o jsPDF inteiro para um módulo que precisa ser puro e leve.
 */
export function formatTemplateCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

/**
 * Data em dd/mm/aaaa. Aceita `Date` ou string. Para strings no formato
 * `YYYY-MM-DD` (o que o Postgres devolve em colunas `date`) faz o split
 * manualmente em vez de `new Date(...)`, que interpretaria como UTC e voltaria
 * um dia no fuso do Brasil — mesmo cuidado de `formatQuoteDate` nos PDFs.
 */
export function formatTemplateDate(value: string | Date): string {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return value.toLocaleDateString('pt-BR');
  }
  const raw = String(value).trim();
  if (!raw) return '';
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('pt-BR');
}

/** Converte o contexto cru no mapa token→texto já formatado. */
function formatContext(context: TemplateContext): Record<string, string> {
  const out: Record<string, string> = {};
  const put = (key: string, value: string | null | undefined) => {
    if (value === null || value === undefined) return;
    const text = String(value);
    if (text) out[key] = text;
  };

  put('cliente', context.cliente);
  put('empresa', context.empresa);
  put('link', context.link);
  put('titulo', context.titulo);
  put('numero', context.numero === null || context.numero === undefined ? null : String(context.numero));
  if (typeof context.valor === 'number' && Number.isFinite(context.valor)) {
    out.valor = formatTemplateCurrency(context.valor);
  }
  if (context.validade) put('validade', formatTemplateDate(context.validade));
  if (context.data) put('data', formatTemplateDate(context.data));

  return out;
}

// ===========================================================================
// Renderização
// ===========================================================================

/** Casa `{token}` com nomes simples — evita capturar `{` solto ou `{ }` com espaço. */
const TOKEN_RE = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;

/**
 * Substitui as variáveis do corpo pelos valores do contexto.
 *
 * Regras (todas cobertas por teste):
 *  1. token CONHECIDO com valor → valor formatado em pt-BR;
 *  2. token CONHECIDO sem valor → string vazia (nunca deixe `{valor}` cru vazar
 *     para o cliente);
 *  3. token DESCONHECIDO → mantido literal, para o usuário ver o próprio erro
 *     de digitação em vez de a mensagem sumir em silêncio;
 *  4. substituição NÃO é recursiva — passe única via `String.replace` com
 *     callback: o texto injetado não é reescaneado, então um cliente chamado
 *     "{valor}" não consegue injetar token nenhum.
 */
export function renderTemplate(body: string, context: TemplateContext = {}): string {
  if (!body) return '';
  const values = formatContext(context);
  const known = new Set(ALL_VARIABLE_KEYS);

  return body.replace(TOKEN_RE, (match, key: string) => {
    if (!known.has(key)) return match; // regra 3
    return values[key] ?? ''; // regras 1 e 2
  });
}

/** Insere um token na posição do cursor. Devolve o novo texto e o novo cursor. */
export function insertToken(
  body: string,
  token: string,
  selectionStart: number,
  selectionEnd: number = selectionStart,
): { body: string; cursor: number } {
  const start = Math.max(0, Math.min(selectionStart, body.length));
  const end = Math.max(start, Math.min(selectionEnd, body.length));
  const next = `${body.slice(0, start)}${token}${body.slice(end)}`;
  return { body: next, cursor: start + token.length };
}

// ===========================================================================
// Modelos padrão (semeados na primeira abertura da tela)
// ===========================================================================

export interface DefaultMessageTemplate {
  name: string;
  category: MessageTemplateCategory;
  body: string;
  is_default: boolean;
}

/**
 * Reproduzem as mensagens que HOJE estão hardcoded nos pontos de envio, para
 * que o comportamento atual não regrida e o usuário já comece com algo útil:
 *  - `quote`  → DashboardPage (card de orçamento) e o novo botão do QuoteDetail;
 *  - `contract` → ContractDetailPage.shareWhatsApp / shareEmail.
 */
export const DEFAULT_TEMPLATES: readonly DefaultMessageTemplate[] = Object.freeze([
  {
    name: 'Envio do orçamento',
    category: 'quote',
    // `{link}` fica na PRÓPRIA LINHA e sem dois-pontos antes: um orçamento
    // criado offline ainda não tem link público (o id é `offline_…`), e assim a
    // mensagem degrada para um texto limpo em vez de terminar em "link: ".
    body:
      'Olá {cliente}! Segue o orçamento {numero} no valor de {valor}.\n{link}',
    is_default: true,
  },
  {
    name: 'Cobrança educada',
    category: 'quote',
    body:
      'Olá {cliente}, tudo bem? Passando para saber se você conseguiu ver o ' +
      'orçamento {numero} no valor de {valor}. Fico à disposição para ajustar ' +
      'o que precisar!',
    is_default: false,
  },
  {
    name: 'Envio do contrato',
    category: 'contract',
    body:
      'Olá {cliente}! Segue o contrato "{titulo}" para você revisar e assinar: {link}',
    is_default: true,
  },
  {
    name: 'Envio do recibo',
    category: 'receipt',
    body:
      'Olá {cliente}! Segue o recibo {numero} no valor de {valor}, referente ao ' +
      'pagamento recebido em {data}. Obrigado pela confiança!',
    is_default: true,
  },
  {
    name: 'Ordem de serviço',
    category: 'service_order',
    body:
      'Olá {cliente}! Passando para falar sobre a ordem de serviço {numero}. ' +
      'Qualquer dúvida, estou à disposição!',
    is_default: true,
  },
]);

/**
 * Corpo padrão de uma categoria — a rede de segurança que garante que NINGUÉM
 * fica sem conseguir enviar: usuário sem PRO, empresa sem nenhum modelo
 * cadastrado, ou modelo recém-excluído.
 */
export function defaultBodyFor(category: MessageTemplateCategory): string {
  const preferred =
    DEFAULT_TEMPLATES.find((t) => t.category === category && t.is_default) ??
    DEFAULT_TEMPLATES.find((t) => t.category === category);
  return preferred?.body ?? 'Olá {cliente}! {link}';
}

// ===========================================================================
// Construção do contexto de um orçamento
// ===========================================================================

/**
 * Interpreta uma data de orçamento em horário LOCAL. `new Date('2026-01-01')` é
 * meia-noite UTC, que no fuso do Brasil é 31/12 — daí o split manual para
 * strings `YYYY-MM-DD`. Timestamps completos (`created_at`) já vêm com fuso e
 * podem ir direto para o `Date`.
 */
function parseLocalDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const raw = String(value).trim();
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Contexto de um ORÇAMENTO. Existe para os dois pontos de envio (o card do
 * dashboard e o botão do QuoteDetail) calcularem `{validade}` do MESMO jeito —
 * a conta "data + validity_days" já divergiu uma vez por o dashboard tentar ler
 * uma coluna `quote_date` que não existe (a data do orçamento é `created_at`).
 */
export function buildQuoteMessageContext(input: {
  clientName?: string | null;
  sequenceId?: number | string | null;
  total?: number | null;
  /** Data do orçamento — `created_at` no banco, `quoteDate` no formulário. */
  issuedAt?: string | Date | null;
  validityDays?: number | string | null;
  /**
   * Id do orçamento. Um id `offline_…` (criação offline ainda não sincronizada)
   * NÃO tem página pública — mandar `/o/offline_123` para o cliente seria um
   * link quebrado, então nesse caso `{link}` fica vazio.
   */
  quoteId?: string | null;
  buildLink?: (quoteId: string) => string;
  companyName?: string | null;
  title?: string | null;
}): TemplateContext {
  const quoteId = input.quoteId ? String(input.quoteId) : '';
  const link =
    quoteId && !quoteId.startsWith('offline_') && input.buildLink ? input.buildLink(quoteId) : '';
  const issued = parseLocalDate(input.issuedAt);

  // `Number(null)` e `Number('')` são 0 — sem esta checagem, um orçamento SEM
  // validade renderizaria `{validade}` como a própria data de emissão ("válido
  // até hoje"), que é pior do que não aparecer nada.
  const rawDays = input.validityDays;
  const hasDays = rawDays !== null && rawDays !== undefined && String(rawDays).trim() !== '';
  const days = hasDays ? Number(rawDays) : Number.NaN;

  let validade: Date | null = null;
  if (issued && Number.isFinite(days)) {
    validade = new Date(issued.getTime());
    validade.setDate(validade.getDate() + days);
  }

  return {
    cliente: input.clientName ?? '',
    numero: input.sequenceId ?? '',
    valor: typeof input.total === 'number' ? input.total : null,
    validade,
    data: issued,
    link,
    empresa: input.companyName ?? '',
    titulo: input.title ?? '',
  };
}

// ===========================================================================
// Seleção do modelo a usar
// ===========================================================================

export interface MessageTemplateRow {
  id: string;
  name: string;
  category: string;
  body: string;
  is_default?: boolean | null;
}

/**
 * Modelo pré-selecionado de uma categoria: o primeiro marcado `is_default`,
 * senão o primeiro da lista, senão `null` (o chamador cai em `defaultBodyFor`).
 * Pegar sempre o PRIMEIRO default faz um estado inconsistente (dois defaults
 * gravados por duas telas simultâneas) degradar para "escolhe um", não para erro.
 */
export function pickDefaultTemplate<T extends MessageTemplateRow>(
  templates: readonly T[],
  category: MessageTemplateCategory,
): T | null {
  const ofCategory = templates.filter((t) => t.category === category);
  if (ofCategory.length === 0) return null;
  return ofCategory.find((t) => t.is_default === true) ?? ofCategory[0];
}

// ===========================================================================
// Telefone e URLs de envio
// ===========================================================================

/**
 * Normaliza um telefone brasileiro para o formato que o `wa.me` espera
 * (só dígitos, com DDI). Helper ÚNICO — antes disso `ContractDetailPage`
 * prefixava `55` e `DashboardPage` não prefixava nada, então o mesmo contato
 * abria conversas diferentes dependendo da tela.
 *
 * A decisão NÃO é `startsWith('55')`: o DDD 55 é de Santa Maria/RS, então
 * `55999999999` (11 dígitos, sem DDI) seria lido como "já tem DDI" por uma
 * checagem ingênua e o link abriria um número truncado. As regras, em ordem:
 *   1. um `+` no começo é DDI EXPLÍCITO → nunca prefixa nada (é o único jeito
 *      de gravar um número estrangeiro, já que 11 dígitos é ambíguo entre um
 *      celular brasileiro com DDD e um número dos EUA com país);
 *   2. 10 ou 11 dígitos → nacional sem DDI → prefixa `55`;
 *   3. demais comprimentos (12, 13, …) → devolve como está; já tem país.
 * Sem `+`, o palpite é sempre Brasil — o app é pt-BR only e essa é a leitura
 * certa em praticamente 100% dos casos reais.
 */
export function normalizeWhatsAppPhone(raw?: string | null): string {
  const text = String(raw ?? '').trim();
  const digits = text.replace(/\D/g, '');
  if (!digits) return '';
  if (text.startsWith('+')) return digits; // regra 1
  if (digits.length === 10 || digits.length === 11) return `55${digits}`; // regra 2
  return digits; // regra 3
}

/**
 * Link do WhatsApp. Codifica a mensagem UMA única vez, sobre o texto JÁ
 * renderizado (ver a nota de ordem no topo do arquivo). Sem telefone, devolve o
 * link "escolher contato" em vez de um link quebrado.
 */
export function buildWhatsAppUrl(phone: string | null | undefined, message: string): string {
  const target = normalizeWhatsAppPhone(phone);
  const text = encodeURIComponent(message);
  return target ? `https://wa.me/${target}?text=${text}` : `https://wa.me/?text=${text}`;
}

/**
 * Link `mailto:`. Mesma regra de codificação única do WhatsApp para assunto e
 * corpo. O endereço NÃO é percent-encoded (clientes de e-mail lidam mal com
 * `%40`), mas é reduzido a um charset seguro para que um endereço malformado não
 * consiga injetar `?`/`&` e forjar outros campos do link.
 */
export function buildMailtoUrl(options: {
  to?: string | null;
  subject: string;
  body: string;
}): string {
  const to = String(options.to ?? '')
    .trim()
    .replace(/[^A-Za-z0-9._%+@-]/g, '');
  const subject = encodeURIComponent(options.subject);
  const body = encodeURIComponent(options.body);
  return `mailto:${to}?subject=${subject}&body=${body}`;
}
